import React, { useState, useEffect, useCallback } from 'react';
import { storageService } from '../../services/storageService';
import { StorageFile } from '../../types/storage';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';

interface ImageLibraryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectImage: (url: string, file?: StorageFile) => void;
}

export const ImageLibraryModal: React.FC<ImageLibraryModalProps> = ({
    isOpen,
    onClose,
    onSelectImage,
}) => {
    const { currentWorkspace } = useWorkspace();
    const { currentUser } = useAuth();
    const [images, setImages] = useState<StorageFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedImage, setSelectedImage] = useState<StorageFile | null>(null);
    const [uploading, setUploading] = useState(false);

    const loadImages = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        setError(null);
        try {
            const files = await storageService.getFiles(
                currentUser.uid,
                currentWorkspace?.id,
                'image',
                100
            );
            setImages(files);
        } catch (err) {
            console.error('Error loading images:', err);
            setError('Failed to load images. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [currentUser, currentWorkspace?.id]);

    useEffect(() => {
        if (isOpen) {
            loadImages();
            setSelectedImage(null);
            setSearchQuery('');
        }
    }, [isOpen, loadImages]);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !currentUser) return;

        setUploading(true);
        try {
            const result = await storageService.uploadEditorImage(
                file,
                currentUser.uid,
                currentWorkspace?.id
            );
            // Reload images to include the new one
            await loadImages();
        } catch (err) {
            console.error('Error uploading image:', err);
            setError('Failed to upload image. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const filteredImages = images.filter((img) =>
        img.originalName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSelect = () => {
        if (selectedImage) {
            onSelectImage(selectedImage.url, selectedImage);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-4xl h-[80vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-900">Image Library</h3>
                        <p className="text-sm text-slate-500">Select an image from your library or upload a new one</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                    >
                        <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Search & Upload Bar */}
                <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50">
                    <div className="relative flex-1">
                        <svg
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search images..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:border-teal-400 focus:ring-1 focus:ring-teal-400 outline-none transition-all"
                        />
                    </div>
                    <label className="cursor-pointer px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2">
                        {uploading ? (
                            <>
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Uploading...
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Upload Image
                            </>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleUpload}
                            className="hidden"
                            disabled={uploading}
                        />
                    </label>
                </div>

                {/* Image Grid */}
                <div className="flex-1 overflow-y-auto p-5">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <svg className="w-8 h-8 mx-auto text-teal-600 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                <p className="mt-2 text-sm text-slate-500">Loading images...</p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <svg className="w-12 h-12 mx-auto text-red-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <p className="text-sm text-slate-600">{error}</p>
                                <button
                                    onClick={loadImages}
                                    className="mt-2 text-sm text-teal-600 hover:text-teal-700"
                                >
                                    Try again
                                </button>
                            </div>
                        </div>
                    ) : filteredImages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <svg className="w-12 h-12 mx-auto text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <p className="text-sm text-slate-500">
                                    {searchQuery ? `No images found for "${searchQuery}"` : 'No images in your library yet'}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">Upload an image to get started</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-4 gap-3">
                            {filteredImages.map((image) => (
                                <button
                                    key={image.id}
                                    onClick={() => setSelectedImage(image)}
                                    className={`group relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${selectedImage?.id === image.id
                                        ? 'border-teal-500 ring-2 ring-teal-200'
                                        : 'border-transparent hover:border-slate-300'
                                        }`}
                                >
                                    <img
                                        src={image.url}
                                        alt={image.originalName}
                                        className="w-full h-full object-cover"
                                    />
                                    {selectedImage?.id === image.id && (
                                        <div className="absolute inset-0 bg-teal-500/20 flex items-center justify-center">
                                            <svg className="w-8 h-8 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    )}
                                    <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                        <p className="text-xs text-white truncate">{image.originalName}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200 bg-slate-50">
                    <span className="text-sm text-slate-500">
                        {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''} in library
                    </span>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSelect}
                            disabled={!selectedImage}
                            className="px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                        >
                            Select Image
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageLibraryModal;
